
import styled, { DefaultTheme } from 'styled-components';
import Button_v2 from '@/components/molecules/Button/index';
import MarkdownParser from '@/components/molecules/MarkdownParser';
import devices from '@/utilities/devices';

interface CtaCardOwnProps {
	/**
	 * The background and theme color of the card.
	 */
	readonly backgroundColor?: 'prune' | 'white';
	/**
	 * CTA object with it's properties.
	 */
	readonly button?: {
		/**
		 * Url link of the cta.
		 */
		readonly url: string;
		/**
		 * Text displayed in the cta.
		 */
		readonly text: string;
	},
	/**
	 * Text description of the card
	 */
	readonly description: string | string[];
	/**
	 * Title of the card.
	 */
	readonly title: string;
}

// #region STYLE
const CtaCardContainer = styled.div<{ backgroundColor?: string }>`
	background-color: ${({ backgroundColor, theme }) => backgroundColor === 'prune'
			? theme.color.pruneLight
			: theme.color.white
	};
	box-shadow: ${({ theme }) => `
		0 0 0.625rem rgba(${theme.color.rgbPrune}, 0.1),
		0 0 3.125rem rgba(${theme.color.rgbPrune}, 0.03),
		0 0 4.375rem rgba(${theme.color.rgbPrune}, 0.02)
	`};
	box-sizing: border-box;
	border-radius: 0.25rem;
	display: flex;
	flex: 1 1 0;
	flex-direction: column;
	min-height: 16rem;
	position: relative;
	text-align: center;
	transition: .25s;

	ul {
		padding-inline-start: 0.625rem;
		text-align: left;
		padding: 0 1.2rem;
	}

	:hover {
		transform: translateY(-3px);
		box-shadow: 0px 0px 20px rgba(63, 6, 43, 0.1), 0px 0px 60px rgba(63, 6, 43, 0.03), 0px 0px 80px rgba(63, 6, 43, 0.02);
	}

	@media screen and (${devices.mobile}) {
		min-height: auto;
		width: 100%;
	}
	@media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
		min-height: auto;
		max-width: 80%;
		width: 32rem;
	}
`;

const CardTitle = styled.h2<{ backgroundColor?: string }>`
	color: ${({ backgroundColor, theme }) => backgroundColor === 'prune'
			? theme.color.beige
			: theme.color.prune
	};
	box-sizing: border-box;
	border-bottom: 0.0625rem solid ${({ backgroundColor, theme }) =>
			backgroundColor === 'prune' ? theme.color.prune : theme.color.grey
	};
	font-size: ${({ theme }) => theme.fontSize.DEFAULT};
	font-weight: bold;
	margin: 0;
	padding: 1.25rem;
	width: 100%;
`;

const CardContent = styled.div<{ withCta?: boolean }>`
	display: flex;
	flex-direction: column;
	height: 100%;
	justify-content: ${({ withCta }) => withCta ? 'space-between' : 'flex-start'};
	.content-container {
		padding: .25rem 1.25rem;
	}

	.button-container {
		padding: ${({ withCta }) => withCta ? '0rem 1.25rem 1.25rem' : '0'};
		@media screen and (${devices.mobile}) {
			padding: ${({ withCta }) => withCta ? '0 1.25rem' : '0'};
		}
	}
`;
const ContentDescription = styled.h4<{ color?: keyof DefaultTheme['color'] }>`
	color: ${({ color, theme }) => theme.color[color as keyof DefaultTheme['color']]};
	font-size: ${({ theme }) => theme.fontSize.DEFAULT};
	font-weight: normal;
	text-align: center;
`;
// #endregion

// #region COMPONENT
const CtaCard = ({ card }: { card: CtaCardOwnProps }) => {
	return (
		<CtaCardContainer
			backgroundColor={card?.backgroundColor}
		>
			<CardTitle backgroundColor={card?.backgroundColor}> {card.title} </CardTitle>
			<CardContent withCta={!!card.button}>
				<div className='content-container'>
					<ContentDescription color={card.backgroundColor === 'prune' ? 'white' : 'greyDarker'}>
						<MarkdownParser>
							{card.description}
						</MarkdownParser>
					</ContentDescription>
				</div>
				<div className='button-container'>
					{card.button && (
						<Button_v2
							text={card.button.text}
							href={card.button.url}
							version='whiteOnRed'
						/>
					)}
				</div>
			</CardContent>
		</CtaCardContainer>
	);
};
// #endregion

export default CtaCard;
